import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { MainLayout } from '../layouts/MainLayout';
import { useAuthContext } from '../contexts/AuthContext';
import { authService } from '../services/api/authService';

export function SettingsPage() {
  const { user, refreshAuth } = useAuthContext();
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // ユーザー情報が更新されたら名前フィールドも更新
  useEffect(() => {
    if (user?.name) {
      setName(user.name);
    }
  }, [user?.name]);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!name.trim()) {
      setError('氏名を入力してください');
      return;
    }

    setProfileLoading(true);
    try {
      await authService.updateProfile({ name: name.trim() });
      await refreshAuth(); // AuthContextのユーザー情報を更新
      setMessage('プロフィールを更新しました');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'プロフィールの更新に失敗しました';
      setError(errorMessage);
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    // バリデーション
    if (!currentPassword) {
      setError('現在のパスワードを入力してください');
      return;
    }

    if (!newPassword) {
      setError('新しいパスワードを入力してください');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('新しいパスワードが一致しません');
      return;
    }

    if (newPassword.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }

    // パスワード強度チェック（英数字混在）
    const hasLetter = /[a-zA-Z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    if (!hasLetter || !hasNumber) {
      setError('パスワードは英字と数字を両方含めてください');
      return;
    }

    setPasswordLoading(true);
    try {
      await authService.changePassword({
        currentPassword,
        newPassword,
      });
      setMessage('パスワードを変更しました');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'パスワードの変更に失敗しました';
      setError(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <MainLayout>
      <Box sx={{ maxWidth: 600 }}>
        <Typography variant="h4" component="h1" fontWeight={600} gutterBottom>
          設定
        </Typography>

        {message && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setMessage('')}>
            {message}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              プロフィール
            </Typography>
            <Box component="form" onSubmit={handleProfileUpdate}>
              <TextField
                fullWidth
                label="氏名"
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="メールアドレス"
                value={user?.email || ''}
                margin="normal"
                disabled
                helperText="メールアドレスは変更できません"
              />
              <Button
                type="submit"
                variant="contained"
                disabled={profileLoading}
                sx={{ mt: 2 }}
              >
                {profileLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'プロフィールを更新'
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              パスワード変更
            </Typography>
            <Box component="form" onSubmit={handlePasswordChange}>
              <TextField
                fullWidth
                label="現在のパスワード"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                margin="normal"
              />
              <Divider sx={{ my: 2 }} />
              <TextField
                fullWidth
                label="新しいパスワード"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                helperText="8文字以上、英数字混在"
              />
              <TextField
                fullWidth
                label="新しいパスワード（確認）"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
              />
              <Button
                type="submit"
                variant="contained"
                disabled={passwordLoading}
                sx={{ mt: 2 }}
              >
                {passwordLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'パスワードを変更'
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
}
